import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NetworkBanner } from './states';
import { StepChips } from './choices';

/**
 * Full-page assistant outside the app frame (design/ObjektAnlegen): title left, numbered steps in the middle,
 * cancel link right; content below, optionally with a sticky aside (preview). Done steps can be revisited.
 */
export function WizardLayout({ title, steps, current, onStep, stepsLabel, cancel, aside, children, after }: {
  title: ReactNode;
  steps: { key: string; label: string }[];
  current: number;
  onStep?: (i: number) => void;
  stepsLabel: string;
  cancel: { to: string; label: string };
  aside?: ReactNode;
  children: ReactNode;
  after?: ReactNode;
}) {
  const { t } = useTranslation('common');
  const titleId = useId();
  return (
    <div className="focus-shell wizard-shell">
      <a className="skip-link" href="#wizard-main">{t('ui.skipToContent')}</a>
      <header className="focus-bar wizard-bar" aria-labelledby={titleId}>
        <h1 id={titleId} className="fb-title">{title}</h1>
        <StepChips steps={steps} current={current} onSelect={onStep} label={stepsLabel} />
        <Link className="wizard-cancel" to={cancel.to}>{cancel.label}</Link>
      </header>
      <main id="wizard-main" className="focus-main" tabIndex={-1}>
        <NetworkBanner />
        <div className={`wizard-body ${aside ? 'has-aside' : ''}`}>
          <div className="wizard-content">{children}</div>
          {aside && <aside className="wizard-aside">{aside}</aside>}
        </div>
      </main>
      {after}
    </div>
  );
}
